import React, { useState, useEffect } from 'react';
import { useNavigate } from "react-router-dom";
import { BASELINE } from '../../util';
import axios from 'axios';
import { toast } from "react-hot-toast";
import { motion, AnimatePresence } from "framer-motion";
import {
    FiArrowLeft, FiBell, FiBookOpen, FiCheckCircle, FiClock,
    FiAlertCircle, FiLogOut, FiList, FiGrid, FiPlay, FiRotateCcw,
    FiBarChart2, FiChevronRight
} from "react-icons/fi";
import { HiOutlineLightningBolt, HiOutlineAcademicCap } from "react-icons/hi";
import CircleGraph from './circlegraph';

// ── Confirm Leave Modal ──────────────────────────────────────────────────────
function ConfirmLeaveModal({ isOpen, onConfirm, onCancel }) {
    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                    className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-slate-900/50 backdrop-blur-sm"
                >
                    <motion.div
                        initial={{ opacity: 0, y: 16, scale: 0.96 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 8, scale: 0.96 }}
                        transition={{ duration: 0.2, ease: "easeOut" }}
                        className="w-full max-w-sm bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden"
                    >
                        <div className="h-1 w-full bg-gradient-to-r from-red-400 to-rose-500" />
                        <div className="p-6">
                            <div className="flex items-center gap-3 mb-3">
                                <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-red-50 border border-red-100">
                                    <FiLogOut size={16} className="text-red-500" />
                                </div>
                                <div>
                                    <h2 className="text-base font-bold text-slate-800">Leave Class?</h2>
                                    <p className="text-xs text-slate-400">This action cannot be undone</p>
                                </div>
                            </div>
                            <p className="text-sm text-slate-500 mb-5 leading-relaxed">
                                You'll be removed from this class and lose access to its assignments.
                            </p>
                            <div className="flex justify-end gap-2">
                                <button onClick={onCancel} className="px-4 py-2 rounded-xl text-sm font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 transition-colors">
                                    Cancel
                                </button>
                                <button onClick={onConfirm} className="px-4 py-2 rounded-xl text-sm font-bold text-white bg-gradient-to-r from-red-500 to-rose-500 hover:from-red-600 hover:to-rose-600 transition-all shadow-sm">
                                    Leave Class
                                </button>
                            </div>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}

// ── Helpers ──────────────────────────────────────────────────────────────────
const levelLabel = (l) => l == 1 ? "Beginner" : l == 2 ? "Intermediate" : l == 3 ? "Advanced" : null;
const fmtDate = (d) => {
    if (!d) return "";
    try { return new Date(d).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" }); }
    catch { return ""; }
};
const fmtDateRelative = (d) => {
    if (!d) return "";
    try {
        const diff = Date.now() - new Date(d).getTime();
        const days = Math.floor(diff / 86400000);
        if (days === 0) return "Today";
        if (days === 1) return "Yesterday";
        if (days < 7) return `${days}d ago`;
        return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric" });
    } catch { return ""; }
};

// ── Status helper ─────────────────────────────────────────────────────────────
const getStatus = (complete, pending) =>
    complete
        ? { label: "Complete",    icon: <FiCheckCircle size={11} />, cls: "bg-emerald-50 border-emerald-200 text-emerald-700", dot: "bg-emerald-400", accent: "#10b981" }
        : pending
        ? { label: "In Progress", icon: <FiClock size={11} />,       cls: "bg-amber-50 border-amber-200 text-amber-700",       dot: "bg-amber-400",  accent: "#f59e0b" }
        : { label: "Not Started", icon: <FiAlertCircle size={11} />, cls: "bg-slate-100 border-slate-200 text-slate-600",      dot: "bg-slate-400",  accent: "#94a3b8" };

// ── Main Component ───────────────────────────────────────────────────────────
function HomeworkView({ setdashboard, user, shortid }) {
    const [homework, setHomework] = useState([]);
    const [classdata, setClassData] = useState(null);
    const [testResults, setTestResults] = useState([]);
    const [autosave, setAutosave] = useState([]);
    const [notifications, setNotifications] = useState([]);
    const [confirmLeave, setConfirmLeave] = useState(false);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('notifications');
    const [viewMode, setViewMode] = useState('grid');
    const [resultPanel, setResultPanel] = useState(null);
    const navigate = useNavigate();

    const fetchNotifications = async (classId) => {
        if (!classId) return;
        try {
            const { data, status } = await axios.get(`${BASELINE}notification/get/${classId}`);
            if (status === 200) setNotifications(data);
        } catch { }
    };

    const fetchTestResults = async () => {
        try {
            const { data } = await axios.post(BASELINE + "testresult/get", { id: user.u_id });
            setTestResults(data);
        } catch { }
    };

    const fetchAutosave = async () => {
        try {
            const { data } = await axios.get(BASELINE + "getautosave", { params: { id: user.u_id } });
            setAutosave(data.data);
        } catch { }
    };

    const fetchHomework = async () => {
        try {
            const { data } = await axios.post(BASELINE + "gethomework", { shortid });
            setHomework(data.matchedHomeworks || []);
            setClassData(data.classData || null);
            if (data.classData?.classid) fetchNotifications(data.classData.classid);
        } catch { } finally { setLoading(false); }
    };

    useEffect(() => {
        if (!user) return;
        fetchTestResults();
        fetchHomework();
        fetchAutosave();
    }, [user]);

    const handleLeaveClass = async () => {
        try {
            await axios.post(BASELINE + 'classdelstudent', [{ classid: classdata.classid, studentid: user.u_id }]);
            toast.success("You left the class.");
            navigate("/main", { state: { goTo: 102 } });
        } catch { toast.error("Could not leave the class. Try again."); }
        setConfirmLeave(false);
    };

    const handleNavigatequiz = (testid, quiz_type, testname) => {
        localStorage.setItem("currentTest", testid);
        localStorage.setItem("type", quiz_type);
        localStorage.setItem("testname", testname);
        localStorage.setItem("hw", true);
        localStorage.setItem("hw_classname", classdata?.class_name || '');
        navigate("/sat/quiz");
    };

    const viewResult = (testid) => {
        const results = testResults.filter(t => t.ori_id === testid).sort((a, b) => new Date(a.date) - new Date(b.date));
        if (results.length === 0) { toast.error("Result not found."); return; }
        setResultPanel(results);
    };

    const startHomework = async (testid, type, name) => {
        const foundAuto = autosave?.find((a) => a.original_tid === testid);
        if (isTestPending(testid)) {
            const ok = window.confirm("You already have saved progress for this test. Start over?");
            if (!ok) return;
            try {
                await axios.delete(`${BASELINE}autosave/${foundAuto.testid}`, { params: { userid: user.u_id } });
                setAutosave(prev => prev.filter(a => a.testid !== foundAuto.testid));
            } catch { toast.error("Could not reset progress. Try again."); return; }
        }
        handleNavigatequiz(testid, type, name);
    };

    const resumeHomework = (testid, type, name) => {
        const foundAuto = autosave?.find((a) => a.original_tid === testid);
        if (!foundAuto) { toast.error("Saved progress not found."); return; }
        localStorage.setItem("currentTest", foundAuto.testid);
        localStorage.setItem("type", type);
        localStorage.setItem("testname", name);
        localStorage.setItem("hw", true);
        localStorage.setItem("hw_classname", classdata?.class_name || '');
        localStorage.setItem("original_text", foundAuto.original_tid);
        navigate("/sat/quiz");
    };

    const isTestComplete = (id) => testResults.some(t => t.ori_id === id);
    const isTestPending = (id) => autosave?.some(a => a.original_tid === id);

    const bgUrl = `${process.env.PUBLIC_URL}/bgimg${parseInt(classdata?.bg) || 1}.png`;
    const completedCount = homework.filter(hw => isTestComplete(hw.testid)).length;
    const pendingCount = homework.filter(hw => !isTestComplete(hw.testid) && isTestPending(hw.testid)).length;
    const completionPct = homework.length > 0 ? Math.round((completedCount / homework.length) * 100) : 0;

    return (
        <div className="h-full overflow-y-auto bg-[#eef2f7]">

            {/* ── Hero Header ─────────────────────────────────────────────── */}
            <div className="relative overflow-hidden" style={{ minHeight: 220 }}>
                <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${bgUrl})` }} />
                <div className="absolute inset-0" style={{ background: "linear-gradient(135deg, rgba(10,24,50,0.95) 0%, rgba(15,40,80,0.88) 100%)" }} />

                {/* subtle grid overlay */}
                <div className="absolute inset-0 opacity-[0.04]"
                    style={{ backgroundImage: 'repeating-linear-gradient(0deg,#fff 0,#fff 1px,transparent 1px,transparent 40px),repeating-linear-gradient(90deg,#fff 0,#fff 1px,transparent 1px,transparent 40px)' }} />

                <div className="relative px-6 pt-5 pb-6">
                    {/* Top bar */}
                    <div className="flex items-center justify-between mb-6">
                        <button
                            onClick={() => navigate("/main", { state: { goTo: 102 } })}
                            className="flex items-center gap-2 px-3 py-2 rounded-xl bg-white/10 hover:bg-white/20 border border-white/15 text-white/70 hover:text-white transition-all text-sm font-medium"
                        >
                            <FiArrowLeft size={15} />
                            <span className="hidden sm:inline">My Classes</span>
                        </button>
                        <button
                            onClick={() => setConfirmLeave(true)}
                            className="flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium bg-red-500/10 hover:bg-red-500/25 border border-red-400/20 text-red-300 hover:text-red-200 transition-all"
                        >
                            <FiLogOut size={13} />
                            Leave
                        </button>
                    </div>

                    {/* Class identity */}
                    <div className="flex items-start gap-4">
                        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-lg shadow-blue-900/40 shrink-0 border border-white/10">
                            <HiOutlineAcademicCap size={26} className="text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap items-center gap-2 mb-1.5">
                                {classdata?.typeclass && (
                                    <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-blue-500/20 border border-blue-400/25 text-blue-200">
                                        {classdata.typeclass}
                                    </span>
                                )}
                                {levelLabel(classdata?.level) && (
                                    <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-violet-500/20 border border-violet-400/25 text-violet-200">
                                        {levelLabel(classdata?.level)}
                                    </span>
                                )}
                                <span className="px-2 py-0.5 rounded-full text-[10px] font-mono bg-white/8 border border-white/10 text-white/40">
                                    {classdata?.shortid || shortid}
                                </span>
                            </div>
                            <h1 className="text-2xl font-bold text-white leading-tight">
                                {loading
                                    ? <span className="inline-block w-48 h-6 rounded-lg animate-pulse bg-white/10" />
                                    : (classdata?.class_name || shortid)}
                            </h1>
                        </div>
                    </div>

                    {/* Stats strip */}
                    {!loading && homework.length > 0 && (
                        <div className="mt-5 grid grid-cols-3 gap-3">
                            {[
                                { label: "Assignments", value: homework.length, color: "text-white" },
                                { label: "Completed",   value: completedCount,  color: "text-emerald-300" },
                                { label: "In Progress", value: pendingCount,    color: "text-amber-300" },
                            ].map(s => (
                                <div key={s.label} className="bg-white/8 border border-white/10 rounded-xl px-4 py-3 backdrop-blur-sm">
                                    <p className={`text-xl font-bold tabular-nums ${s.color}`}>{s.value}</p>
                                    <p className="text-[10px] text-white/45 uppercase tracking-wider font-semibold mt-0.5">{s.label}</p>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Completion bar */}
                    {!loading && homework.length > 0 && (
                        <div className="mt-3">
                            <div className="flex justify-between text-[10px] text-white/40 mb-1 font-medium">
                                <span>Overall progress</span>
                                <span>{completionPct}%</span>
                            </div>
                            <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                                <motion.div
                                    className="h-full rounded-full"
                                    style={{ background: "linear-gradient(90deg, #34d399, #059669)" }}
                                    initial={{ width: 0 }}
                                    animate={{ width: `${completionPct}%` }}
                                    transition={{ duration: 0.8, ease: "easeOut", delay: 0.3 }}
                                />
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* ── Tab Bar ─────────────────────────────────────────────────── */}
            <div className="sticky top-0 z-10 bg-white border-b border-slate-200 shadow-sm">
                <div className="px-6 flex items-center gap-1">
                    {[
                        { id: 'notifications', label: 'Notifications', icon: FiBell, count: notifications.length },
                        { id: 'assignments', label: 'Assignments', icon: FiBookOpen, count: homework.length },
                    ].map(tab => {
                        const Icon = tab.icon;
                        const isActive = activeTab === tab.id;
                        return (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`relative flex items-center gap-2 px-4 py-3.5 text-sm font-semibold transition-colors ${
                                    isActive ? 'text-blue-600' : 'text-slate-500 hover:text-slate-700'
                                }`}
                            >
                                <Icon size={14} />
                                {tab.label}
                                {tab.count > 0 && (
                                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center ${
                                        isActive
                                            ? 'bg-blue-100 text-blue-600'
                                            : 'bg-slate-100 text-slate-500'
                                    }`}>{tab.count}</span>
                                )}
                                {isActive && (
                                    <motion.div
                                        layoutId="tab-indicator"
                                        className="absolute bottom-0 left-0 right-0 h-[2px] bg-blue-600 rounded-t-full"
                                    />
                                )}
                            </button>
                        );
                    })}

                    {/* View toggle — only on assignments tab */}
                    {activeTab === 'assignments' && (
                        <div className="ml-auto flex items-center gap-1 p-1 rounded-lg bg-slate-100 border border-slate-200">
                            <button
                                onClick={() => setViewMode('list')}
                                className={`p-1.5 rounded-md transition-all ${viewMode === 'list' ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}
                            >
                                <FiList size={13} />
                            </button>
                            <button
                                onClick={() => setViewMode('grid')}
                                className={`p-1.5 rounded-md transition-all ${viewMode === 'grid' ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}
                            >
                                <FiGrid size={13} />
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* ── Content ─────────────────────────────────────────────────── */}
            <div className="px-6 py-5">
                <AnimatePresence mode="wait">

                    {/* ASSIGNMENTS TAB */}
                    {activeTab === 'assignments' && (
                        <motion.div key="assignments" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.2 }}>
                            {loading ? (
                                <div className={viewMode === 'grid' ? "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" : "space-y-3"}>
                                    {[1, 2, 3].map(i => (
                                        <div key={i} className={`rounded-2xl animate-pulse bg-slate-200 ${viewMode === 'grid' ? 'h-44' : 'h-20'}`} />
                                    ))}
                                </div>
                            ) : homework.length === 0 ? (
                                <div className="flex flex-col items-center justify-center py-24 text-center">
                                    <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4 bg-gradient-to-br from-blue-50 to-indigo-100 border border-blue-100 shadow-sm">
                                        <HiOutlineLightningBolt size={28} className="text-blue-400" />
                                    </div>
                                    <p className="text-base font-bold text-slate-700 mb-1">No assignments yet</p>
                                    <p className="text-sm text-slate-400">Your teacher hasn't posted any assignments.</p>
                                </div>
                            ) : viewMode === 'list' ? (
                                <div className="space-y-3">
                                    {homework.map((hw, idx) => {
                                        const { name, testid, type } = hw;
                                        const complete = isTestComplete(testid);
                                        const pending = isTestPending(testid);
                                        const status = getStatus(complete, pending);
                                        return (
                                            <motion.div
                                                key={testid || idx}
                                                initial={{ opacity: 0, y: 8 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{ delay: idx * 0.04 }}
                                                className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-shadow"
                                            >
                                                <div className="h-1 w-full" style={{ background: status.accent }} />
                                                <div className="px-5 py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                                                    <div className="flex items-center gap-4 flex-1 min-w-0">
                                                        {/* Number badge */}
                                                        <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 font-bold text-sm border"
                                                            style={{ background: `${status.accent}15`, borderColor: `${status.accent}30`, color: status.accent }}>
                                                            {idx + 1}
                                                        </div>
                                                        <div className="min-w-0">
                                                            <div className="flex flex-wrap items-center gap-1.5 mb-1">
                                                                {type && (
                                                                    <span className="px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wide bg-blue-50 border border-blue-100 text-blue-600">
                                                                        {type}
                                                                    </span>
                                                                )}
                                                                <span className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${status.cls}`}>
                                                                    {status.icon} {status.label}
                                                                </span>
                                                            </div>
                                                            <p className="text-sm font-bold text-slate-800 truncate">{name}</p>
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-2 shrink-0">
                                                        {complete && <>
                                                            <ActionBtn onClick={() => viewResult(testid)} color="indigo">
                                                                <FiBarChart2 size={12} /> Result
                                                            </ActionBtn>
                                                            <ActionBtn onClick={() => handleNavigatequiz(testid, type, name)} color="slate">
                                                                <FiRotateCcw size={12} /> Retake
                                                            </ActionBtn>
                                                        </>}
                                                        {!complete && !pending && (
                                                            <ActionBtn onClick={() => startHomework(testid, type, name)} color="blue">
                                                                <FiPlay size={12} /> Start
                                                            </ActionBtn>
                                                        )}
                                                        {pending && <>
                                                            <ActionBtn onClick={() => resumeHomework(testid, type, name)} color="emerald">
                                                                <FiChevronRight size={12} /> Resume
                                                            </ActionBtn>
                                                            <ActionBtn onClick={() => startHomework(testid, type, name)} color="slate">
                                                                <FiRotateCcw size={12} /> Restart
                                                            </ActionBtn>
                                                        </>}
                                                    </div>
                                                </div>
                                            </motion.div>
                                        );
                                    })}
                                </div>
                            ) : (
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                                    {homework.map((hw, idx) => {
                                        const { name, testid, type } = hw;
                                        const complete = isTestComplete(testid);
                                        const pending = isTestPending(testid);
                                        const status = getStatus(complete, pending);
                                        return (
                                            <motion.div
                                                key={testid || idx}
                                                initial={{ opacity: 0, scale: 0.96 }}
                                                animate={{ opacity: 1, scale: 1 }}
                                                transition={{ delay: idx * 0.05 }}
                                                className="bg-white border border-slate-200 rounded-2xl overflow-hidden flex flex-col shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200"
                                            >
                                                {/* Color top bar */}
                                                <div className="h-1.5 w-full" style={{ background: status.accent }} />

                                                {/* Card body */}
                                                <div className="p-4 flex flex-col gap-3 flex-1">
                                                    <div className="flex items-center justify-between">
                                                        <div className="w-9 h-9 rounded-xl flex items-center justify-center font-bold text-sm border"
                                                            style={{ background: `${status.accent}15`, borderColor: `${status.accent}30`, color: status.accent }}>
                                                            {idx + 1}
                                                        </div>
                                                        {type && (
                                                            <span className="px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wide bg-blue-50 border border-blue-100 text-blue-600">
                                                                {type}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <p className="text-sm font-bold text-slate-800 leading-snug flex-1 line-clamp-2">{name}</p>

                                                    <div className="pt-3 border-t border-slate-100 flex items-center justify-between">
                                                        <span className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${status.cls}`}>
                                                            {status.icon} {status.label}
                                                        </span>
                                                        <div className="flex items-center gap-1.5">
                                                            {complete && <>
                                                                <ActionBtn onClick={() => viewResult(testid)} color="indigo" small>Result</ActionBtn>
                                                                <ActionBtn onClick={() => handleNavigatequiz(testid, type, name)} color="slate" small>Retake</ActionBtn>
                                                            </>}
                                                            {!complete && !pending && <ActionBtn onClick={() => startHomework(testid, type, name)} color="blue" small>Start</ActionBtn>}
                                                            {pending && <>
                                                                <ActionBtn onClick={() => resumeHomework(testid, type, name)} color="emerald" small>Resume</ActionBtn>
                                                                <ActionBtn onClick={() => startHomework(testid, type, name)} color="slate" small>Restart</ActionBtn>
                                                            </>}
                                                        </div>
                                                    </div>
                                                </div>
                                            </motion.div>
                                        );
                                    })}
                                </div>
                            )}
                        </motion.div>
                    )}

                    {/* NOTIFICATIONS TAB */}
                    {activeTab === 'notifications' && (
                        <motion.div key="notifications" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.2 }}>
                            {loading ? (
                                <div className="space-y-4">
                                    {[1, 2, 3].map(i => <div key={i} className="h-28 rounded-2xl animate-pulse bg-slate-200" />)}
                                </div>
                            ) : notifications.length === 0 ? (
                                <div className="flex flex-col items-center justify-center py-24 text-center">
                                    <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4 bg-slate-100 border border-slate-200">
                                        <FiBell size={28} className="text-slate-300" />
                                    </div>
                                    <p className="text-base font-bold text-slate-600 mb-1">All caught up!</p>
                                    <p className="text-sm text-slate-400">No notifications from your teacher yet.</p>
                                </div>
                            ) : (
                                <div className="max-w-3xl space-y-4">
                                    {notifications.map((n, idx) => {
                                        const palettes = [
                                            { from: "#6366f1", to: "#818cf8", light: "#eef2ff", border: "#c7d2fe", text: "#4338ca" },
                                            { from: "#10b981", to: "#34d399", light: "#ecfdf5", border: "#a7f3d0", text: "#065f46" },
                                            { from: "#a855f7", to: "#c084fc", light: "#faf5ff", border: "#e9d5ff", text: "#7e22ce" },
                                            { from: "#3b82f6", to: "#60a5fa", light: "#eff6ff", border: "#bfdbfe", text: "#1d4ed8" },
                                            { from: "#f59e0b", to: "#fbbf24", light: "#fffbeb", border: "#fde68a", text: "#92400e" },
                                        ];
                                        const p = palettes[idx % palettes.length];
                                        return (
                                            <motion.div
                                                key={n.id || idx}
                                                initial={{ opacity: 0, y: 12 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{ delay: idx * 0.06, ease: "easeOut" }}
                                                className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-shadow"
                                            >
                                                {/* Gradient top bar */}
                                                <div className="h-1" style={{ background: `linear-gradient(90deg, ${p.from}, ${p.to})` }} />

                                                <div className="p-5">
                                                    <div className="flex items-start gap-4">
                                                        {/* Icon */}
                                                        <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 border"
                                                            style={{ background: p.light, borderColor: p.border }}>
                                                            <FiBell size={16} style={{ color: p.from }} />
                                                        </div>

                                                        <div className="flex-1 min-w-0">
                                                            {/* Title row */}
                                                            <div className="flex items-start justify-between gap-3 mb-2">
                                                                <h3 className="text-base font-bold text-slate-800 leading-snug">{n.title}</h3>
                                                                <div className="shrink-0 flex flex-col items-end gap-1">
                                                                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full border"
                                                                        style={{ background: p.light, borderColor: p.border, color: p.text }}>
                                                                        {fmtDateRelative(n.date)}
                                                                    </span>
                                                                    <span className="text-[10px] text-slate-400">{fmtDate(n.date)}</span>
                                                                </div>
                                                            </div>

                                                            {/* Body */}
                                                            <div
                                                                className="text-sm text-slate-600 leading-relaxed prose prose-sm max-w-none"
                                                                dangerouslySetInnerHTML={{ __html: n.context }}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            </motion.div>
                                        );
                                    })}
                                </div>
                            )}
                        </motion.div>
                    )}

                </AnimatePresence>
            </div>

            <ConfirmLeaveModal isOpen={confirmLeave} onConfirm={handleLeaveClass} onCancel={() => setConfirmLeave(false)} />

            <AnimatePresence>
                {resultPanel && <ResultPanel results={resultPanel} onClose={() => setResultPanel(null)} />}
            </AnimatePresence>
        </div>
    );
}

// ── Action Button ──────────────────────────────────────────────────────────────
function ActionBtn({ onClick, children, color = "slate", small = false }) {
    const colorMap = {
        blue:    "bg-blue-600 hover:bg-blue-500 text-white shadow-sm shadow-blue-200",
        indigo:  "bg-indigo-50 hover:bg-indigo-100 text-indigo-700 border border-indigo-200",
        emerald: "bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-200",
        slate:   "bg-slate-100 hover:bg-slate-200 text-slate-600",
    };
    return (
        <button
            onClick={onClick}
            className={`flex items-center gap-1.5 rounded-xl font-bold transition-all ${small ? 'px-2.5 py-1 text-[11px]' : 'px-3.5 py-1.5 text-xs'} ${colorMap[color]}`}
        >
            {children}
        </button>
    );
}

// ── Result Panel ──────────────────────────────────────────────────────────────
function ResultPanel({ results, onClose }) {
    const [attemptIdx, setAttemptIdx] = useState(results.length - 1);
    const result = results[attemptIdx];

    const [quizData, setQuizData] = useState(() => {
        try { return JSON.parse(result.quiz_data); } catch { return []; }
    });

    const [isDetailOpen, setIsDetailOpen] = useState(false);
    const [selectedIdx, setSelectedIdx] = useState(null);
    const [selectedQ, setSelectedQ] = useState(null);
    const [loadingQ, setLoadingQ] = useState(false);
    const [picked, setPicked] = useState('');
    const [hideExplanation, setHideExplanation] = useState(false);

    useEffect(() => {
        try { setQuizData(JSON.parse(result.quiz_data)); } catch { setQuizData([]); }
        setIsDetailOpen(false);
        setSelectedIdx(null);
        setSelectedQ(null);
        setPicked('');
    }, [attemptIdx]);

    const type = result.type || 'SAT';
    const correct = (() => { try { return JSON.parse(result.Correct_questionIds || '[]').length; } catch { return quizData.filter(q => q.correct).length; } })();
    const wrong = (() => { try { return JSON.parse(result.Incorrect_questionIds || '[]').length; } catch { return quizData.filter(q => !q.correct).length; } })();
    const total = correct + wrong;
    const pct = total > 0 ? Math.round((correct / total) * 100) : 0;
    const pctColor = pct >= 75 ? 'text-emerald-600' : pct >= 50 ? 'text-blue-600' : 'text-red-500';
    const barColor = pct >= 75 ? '#10b981' : pct >= 50 ? '#3b82f6' : '#ef4444';

    const currentTag = selectedIdx !== null && quizData[selectedIdx]
        ? (quizData[selectedIdx].tag === 'math' ? 'Math' : 'Eng') : 'Eng';

    const fetchQuestion = async (idx) => {
        const entry = quizData[idx];
        if (!entry) return;
        setLoadingQ(true);
        setSelectedIdx(idx);
        setSelectedQ(null);
        setPicked(entry.selectedOption || '');
        try {
            const t = type.toLowerCase();
            let detail = null;
            if (t.includes('act')) {
                const res = await axios.get(BASELINE + 'questions/act', { params: { id: entry.qid } });
                const raw = Array.isArray(res.data) ? res.data[0] : res.data;
                detail = raw ? { ...raw, ...entry } : entry;
            } else if (t.includes('ap')) {
                const res = await axios.get(BASELINE + 'questions/ap', { params: { id: [entry.question_id] } });
                const arr = Array.isArray(res.data[0]) ? res.data[0] : Array.isArray(res.data) ? res.data : [];
                detail = arr[0] ? { ...arr[0], ...entry } : entry;
            } else {
                const section = entry.tag === 'math' ? 'Math' : 'Eng';
                const ep = t.includes('89') ? 'questions/sat89' : t.includes('10') ? 'questions/sat10' : 'questions/sat';
                const res = await axios.get(BASELINE + ep, { params: { id: [entry.questionIds], section } });
                const arr = Array.isArray(res.data[0]) ? res.data[0] : Array.isArray(res.data) ? res.data : [];
                detail = arr[0] ? { ...arr[0], ...entry } : entry;
            }
            setSelectedQ(detail);
        } catch { toast.error('Failed to load question'); }
        finally { setLoadingQ(false); }
    };

    const handleButtonClick = (idx) => { fetchQuestion(idx); setIsDetailOpen(true); };
    const handlePrev = () => { if (selectedIdx > 0) fetchQuestion(selectedIdx - 1); };
    const handleNext = () => { if (selectedIdx < quizData.length - 1) fetchQuestion(selectedIdx + 1); };
    const getReportPath = () => {
        const t = type.toLowerCase();
        const id = result.test_id;
        if (t.includes('act')) return `/results/act/${id}`;
        if (t.includes('ap')) return `/results/ap/quiz/${id}`;
        return `/results/sat/quiz/${id}`;
    };

    return (
        <>
            <motion.div
                className="fixed inset-0 z-[50] flex justify-end"
                initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
                transition={{ type: "tween", duration: 0.4 }}
            >
                <div className="fixed inset-0" aria-hidden="true" onClick={onClose}>
                    <div className="absolute inset-0 bg-slate-900/30 backdrop-blur-sm" />
                </div>

                <div className="relative bg-white w-full md:w-[40%] h-full overflow-y-auto scrollbar-hide border-l border-slate-200 shadow-2xl">
                    <div className="h-[3px]" style={{ background: "linear-gradient(90deg,#2563eb,#7c3aed,#0ea5e9)" }} />

                    <div className="p-5">
                        <button
                            className="absolute top-5 right-5 w-8 h-8 flex items-center justify-center rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-500 hover:text-slate-700 transition-colors"
                            onClick={onClose}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>

                        <p className="font-bold text-xl text-slate-900 pr-10">{result.test_name}</p>

                        {results.length > 1 && (
                            <div className="mt-3 mb-1 flex flex-wrap gap-2">
                                {results.map((r, i) => {
                                    const isSelected = i === attemptIdx;
                                    const isLatest = i === results.length - 1;
                                    return (
                                        <button key={i} onClick={() => setAttemptIdx(i)}
                                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                                                isSelected ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                            }`}>
                                            {i + 1}{i === 0 ? 'st' : i === 1 ? 'nd' : i === 2 ? 'rd' : 'th'} Attempt
                                            {isLatest && <span className="text-[9px] font-bold bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full">Latest</span>}
                                        </button>
                                    );
                                })}
                            </div>
                        )}

                        <div className="mt-4 mb-5 bg-slate-50 border border-slate-200 rounded-xl p-4">
                            <div className="flex items-center gap-5">
                                <div className="shrink-0">
                                    <CircleGraph correctQuestions={correct} wrongQuestions={wrong} />
                                </div>
                                <div className="flex-1 flex flex-col gap-2 min-w-0">
                                    <div className="bg-white border border-slate-100 rounded-xl p-3">
                                        <p className="text-[10px] text-slate-400 uppercase tracking-wider mb-1">Score</p>
                                        <div className="flex items-center gap-2">
                                            <span className={`text-xl font-bold tabular-nums ${pctColor}`}>{pct}%</span>
                                            <div className="flex-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                                                <div className="h-full rounded-full transition-all duration-700" style={{ width: `${pct}%`, background: barColor }} />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-2">
                                        <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-3">
                                            <p className="text-[10px] text-emerald-500 uppercase tracking-wider mb-1">Correct</p>
                                            <span className="text-lg font-bold text-emerald-600">{correct}</span>
                                        </div>
                                        <div className="bg-red-50 border border-red-100 rounded-xl p-3">
                                            <p className="text-[10px] text-red-400 uppercase tracking-wider mb-1">Wrong</p>
                                            <span className="text-lg font-bold text-red-500">{wrong}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
                            <div className="flex items-center justify-between mb-3">
                                <div>
                                    <p className="text-sm font-bold text-slate-800">Question Overview</p>
                                    <div className="flex items-center gap-2 mt-1">
                                        <span className="flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700">
                                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 inline-block" />{correct} Correct
                                        </span>
                                        <span className="flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-red-50 border border-red-200 text-red-600">
                                            <span className="w-1.5 h-1.5 rounded-full bg-red-400 inline-block" />{wrong} Wrong
                                        </span>
                                    </div>
                                </div>
                                <button
                                    className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold bg-indigo-50 border border-indigo-200 text-indigo-700 hover:bg-indigo-100 transition-colors"
                                    onClick={() => window.open(getReportPath(), '_blank')}
                                >
                                    Report
                                </button>
                            </div>
                            <div className="max-h-[420px] overflow-y-auto">
                                <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 lg:grid-cols-8 gap-2.5 pb-2">
                                    {quizData.map((question, index) => (
                                        <div key={index} className="flex flex-col items-center gap-1">
                                            <button
                                                onClick={() => handleButtonClick(index)}
                                                className={`w-11 h-11 rounded-xl flex items-center justify-center font-bold text-sm text-white transition-all hover:scale-105 shadow-sm ${
                                                    question.correct ? 'bg-emerald-500 hover:bg-emerald-400' : 'bg-red-400 hover:bg-red-500'
                                                }`}
                                            >
                                                {index + 1}
                                            </button>
                                            <span className="text-[9px] text-slate-400 text-center leading-tight w-full truncate">
                                                {question.topic}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </motion.div>

            <div className={`fixed top-0 left-0 h-full z-[60] transform transition-transform duration-300 ease-out w-full md:w-[59%] ${isDetailOpen ? 'translate-x-0' : '-translate-x-full'}`}>
                <div className="relative bg-white w-full h-full overflow-auto">
                    <div className="flex items-center justify-between px-5 py-3 bg-slate-50 border-b border-slate-200">
                        <div className="flex items-center gap-3">
                            <span className="text-sm font-semibold text-slate-700">{currentTag === 'Math' ? 'Math' : 'Reading & Writing'}</span>
                            <button className="px-3 py-1.5 rounded-lg text-xs font-medium bg-white border border-slate-200 text-slate-600 hover:bg-slate-100 transition-colors" onClick={handlePrev}>← Prev</button>
                            <button className="px-3 py-1.5 rounded-lg text-xs font-medium bg-white border border-slate-200 text-slate-600 hover:bg-slate-100 transition-colors" onClick={handleNext}>Next →</button>
                            <label className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-white border border-slate-200 text-slate-600 cursor-pointer hover:bg-slate-100 transition-colors">
                                <input type="checkbox" checked={hideExplanation} onChange={e => setHideExplanation(e.target.checked)} className="h-3.5 w-3.5" />
                                Hide Answer
                            </label>
                        </div>
                        <button className="p-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-500 transition-colors" onClick={() => setIsDetailOpen(false)}>
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="h-5 w-5">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>

                    {loadingQ ? (
                        <div className="flex items-center justify-center h-40">
                            <div className="w-6 h-6 rounded-full border-2 border-blue-200 border-t-blue-600 animate-spin" />
                        </div>
                    ) : selectedQ ? (
                        <div className="flex flex-col md:flex-row mt-5 mb-20 sm:mb-0">
                            <div className="md:w-1/2 w-full pb-4 md:pb-0 border-b-2 md:border-b-0 md:border-r border-slate-200 md:pr-2">
                                {selectedQ.stimulus ? (
                                    <div dangerouslySetInnerHTML={{ __html: selectedQ.stimulus }} className="mb-12 sm:mb-0 max-h-[500px] overflow-y-auto ml-5 mr-5 mt-2.5 text-slate-700 text-sm leading-relaxed" />
                                ) : selectedQ.content ? (
                                    <div dangerouslySetInnerHTML={{ __html: selectedQ.content }} className="ml-5 mr-5 max-h-[500px] overflow-y-auto mt-2.5 text-slate-700 text-sm leading-relaxed" />
                                ) : selectedQ.prompt ? (
                                    <div dangerouslySetInnerHTML={{ __html: selectedQ.prompt }} className="ml-5 text-slate-700 text-sm leading-relaxed" />
                                ) : null}
                            </div>

                            <div className="md:w-1/2 w-full md:ml-5 md:mr-5">
                                <div className="mt-4 mb-4 ml-4">
                                    <h2 className="text-lg font-bold text-slate-900">Question Review</h2>
                                    <div className="mt-1 h-0.5 w-12 bg-blue-500 rounded-full" />
                                </div>

                                <div className="my-2 max-w-[560px] overflow-y-auto">
                                    {selectedQ.stem && <div dangerouslySetInnerHTML={{ __html: selectedQ.stem }} className="ml-5 text-slate-700 text-sm leading-relaxed" />}
                                    {!selectedQ.stem && selectedQ.prompt && <div dangerouslySetInnerHTML={{ __html: selectedQ.prompt }} className="ml-5 text-slate-700 text-sm leading-relaxed" />}

                                    {['a', 'b', 'c', 'd'].map((letter) => {
                                        const val = selectedQ[letter];
                                        if (val == null || String(val) === 'null') return null;
                                        return (
                                            <div key={letter} className="px-3 py-1">
                                                <div className="flex items-start gap-3 w-full border border-slate-200 rounded-xl p-3 bg-white hover:bg-slate-50 transition-colors shadow-sm">
                                                    <span className="shrink-0 flex items-center justify-center w-7 h-7 rounded-full border border-slate-300 bg-slate-100 text-slate-600 text-xs font-bold">
                                                        {letter.toUpperCase()}
                                                    </span>
                                                    <div className="flex-1 text-sm text-slate-700 leading-relaxed" dangerouslySetInnerHTML={{ __html: val }} />
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>

                                {picked && selectedQ?.correct_answer && (() => {
                                    const userPicked = picked.charAt(0).toUpperCase() + picked.slice(1);
                                    const correctAns = selectedQ.correct_answer.slice(2, -2);
                                    const isCorrect = userPicked.toUpperCase() === correctAns.toUpperCase();
                                    return (
                                        <div className={`mx-5 my-3 flex items-center gap-3 rounded-xl px-4 py-3 border ${isCorrect ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}>
                                            <span className={`text-lg font-bold ${isCorrect ? 'text-emerald-500' : 'text-red-500'}`}>{isCorrect ? '✓' : '✗'}</span>
                                            <div className="text-sm flex items-center gap-1.5 flex-wrap">
                                                <span className="text-slate-500">Your answer:</span>
                                                <span className={`font-bold px-2 py-0.5 rounded-md ${isCorrect ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>{userPicked}</span>
                                                {!isCorrect && <>
                                                    <span className="text-slate-300">·</span>
                                                    <span className="text-slate-500">Correct:</span>
                                                    <span className="font-bold px-2 py-0.5 rounded-md bg-emerald-100 text-emerald-700">{correctAns}</span>
                                                </>}
                                            </div>
                                        </div>
                                    );
                                })()}

                                {!hideExplanation && (selectedQ?.rationale2 || selectedQ?.rationale || selectedQ?.expl) && (
                                    <div className="mt-5 mb-10 ml-6 mr-6 text-sm text-slate-700 leading-relaxed">
                                        <div dangerouslySetInnerHTML={{ __html: selectedQ.rationale2 || selectedQ.rationale || selectedQ.expl }} />
                                    </div>
                                )}
                            </div>
                        </div>
                    ) : null}
                </div>
            </div>
        </>
    );
}

export default HomeworkView;
